import React, { useEffect, useId, useState } from 'react';
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer,
  XAxis, YAxis,
} from 'recharts';
import { ReferenceDay, ReferenceKind, ReferenceResponse, ReferenceSeries, ReferenceStats } from '../types/referenceData';
import { getReferenceData, referenceErrorText } from '../utils/referenceDataApi';
import { ANALYSIS_CHART_LEFT_MARGIN, ANALYSIS_LEGEND_WRAPPER_STYLE, RUNTIME_CHART_RIGHT_MARGIN } from './analysisChartLayout';
import Button from './ui/Button';
import ChartTooltip from './AnalysisTooltip';

interface Props {
  deviceName: string;
  startDate: string;
  endDate: string;
  referenceError?: string;
}

type Metric = keyof Pick<ReferenceDay, 'indoor_temp_f' | 'indoor_rh' | 'temp_f' | 'rh' | 'pm25_ugm3' | 'co2_ppm' | 'tvoc_ppb'>;
interface Point { date: string; day: ReferenceDay | null }

const SOURCE_ORDER: ReferenceKind[] = ['thermostat', 'sensor_kit', 'air_monitor'];
const AIR_METRICS: { key: Metric; label: string; unit: string }[] = [
  { key: 'pm25_ugm3', label: 'PM2.5', unit: 'µg/m³' },
  { key: 'co2_ppm', label: 'CO2', unit: 'ppm' },
  { key: 'tvoc_ppb', label: 'TVOC', unit: 'ppb' },
];
const PLOT_MARGIN = { top: 10, right: RUNTIME_CHART_RIGHT_MARGIN, left: ANALYSIS_CHART_LEFT_MARGIN, bottom: 60 };
const axisLabel = (value: string, side: 'left' | 'right') => {
  const position: 'insideLeft' | 'insideRight' = side === 'left' ? 'insideLeft' : 'insideRight';
  return { value, angle: side === 'left' ? -90 : 90, position, offset: side === 'left' ? -35 : 75 };
};

const Coverage: React.FC<{ day: ReferenceDay }> = ({ day }) => (
  <p>Coverage: {day.coverage.intervals} of {day.coverage.expected} readings</p>
);

const Tip: React.FC<{ point?: Point; fields: { label: string; value: number | ReferenceStats | undefined; unit: string }[] }> = ({ point, fields }) => {
  if (!point?.day) return null;
  return (
    <div className="bg-card border border-border rounded-sm shadow-lg p-3 text-sm">
      <p className="font-semibold">{point.date}</p>
      <Coverage day={point.day} />
      {fields.map(({ label, value, unit }) => {
        if (value == null) return null;
        return <p key={label}>{label}: {typeof value === 'number' ? `${value} ${unit}` : `min ${value.min} ${unit}, avg ${value.avg} ${unit}, max ${value.max} ${unit}`}</p>;
      })}
    </div>
  );
};

const ChartFrame: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const titleId = useId();
  return (
    <div className="w-full mt-4 mb-8">
      <h4 id={titleId} className="text-lg font-semibold mb-4">{title}</h4>
      <div role="group" aria-labelledby={titleId} className="w-full h-80">{children}</div>
    </div>
  );
};

const chartAxes = (left: string, right?: string) => [
  <CartesianGrid key="grid" strokeDasharray="3 3" yAxisId="left" />,
  <XAxis key="dates" dataKey="date" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={50} />,
  <YAxis key="left" yAxisId="left" domain={[0, 'auto']} label={axisLabel(left, 'left')} />,
  ...(right ? [<YAxis key="right" yAxisId="right" orientation="right" domain={[0, 'auto']} label={axisLabel(right, 'right')} />] : []),
];

const RuntimePlot: React.FC<{ source: ReferenceSeries; points: Point[] }> = ({ source, points }) => {
  const data = points.map(({ date, day }) => ({
    date, day,
    heat: day?.heat_min ?? null,
    aux: day?.aux_heat_min ?? null,
    cool: day?.cool_min ?? null,
    fan: day?.fan_min ?? null,
  }));
  const title = `${source.label} — Runtime per day`;
  const hasAux = points.some(point => point.day?.aux_heat_min != null);
  return (
    <ChartFrame title={title}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={PLOT_MARGIN} accessibilityLayer>
          {chartAxes('Runtime (minutes)')}
          <ChartTooltip content={({ active, label }) => {
            const row = data.find(point => point.date === label);
            if (!active || !row?.day) return null;
            return <Tip point={{ date: row.date, day: row.day }} fields={[
              { label: 'Heat', value: row.heat ?? undefined, unit: 'min' },
              { label: 'Aux heat', value: row.aux ?? undefined, unit: 'min' },
              { label: 'Cool', value: row.cool ?? undefined, unit: 'min' },
              { label: 'Fan', value: row.fan ?? undefined, unit: 'min' },
            ]} />;
          }} />
          <Legend verticalAlign="top" height={20} wrapperStyle={ANALYSIS_LEGEND_WRAPPER_STYLE} />
          <Bar dataKey="heat" name="Heat" fill="var(--chart-heat)" yAxisId="left" stackId="heat" />
          {hasAux && <Bar dataKey="aux" name="Aux heat" fill="var(--chart-aux-heat)" yAxisId="left" stackId="heat" />}
          <Bar dataKey="cool" name="Cool" fill="var(--chart-cool)" yAxisId="left" />
          {source.kind === 'thermostat' && <Line dataKey="fan" name="Fan" stroke="var(--chart-5)" yAxisId="left" connectNulls={false} />}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
};

const StatsPlot: React.FC<{ title: string; points: Point[]; metrics: { key: Metric; label: string; unit: string; axis: 'left' | 'right' }[]; left: string; right?: string }> = ({ title, points, metrics, left, right }) => {
  const data = points.map(({ date, day }) => {
    const values: Record<string, number | ReferenceDay | string | null> = { date, day };
    for (const metric of metrics) {
      const stats = day?.[metric.key];
      values[`${metric.key}_avg`] = stats?.avg ?? null;
      values[`${metric.key}_max`] = stats?.max ?? null;
    }
    return values;
  });
  return (
    <ChartFrame title={title}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={PLOT_MARGIN} accessibilityLayer>
          {chartAxes(left, right)}
          <ChartTooltip content={({ active, label }) => {
            const point = points.find(item => item.date === label);
            if (!active || !point?.day) return null;
            return <Tip point={point} fields={metrics.map(metric => ({
              label: metric.label, value: point.day?.[metric.key], unit: metric.unit,
            }))} />;
          }} />
          <Legend verticalAlign="top" height={20} wrapperStyle={ANALYSIS_LEGEND_WRAPPER_STYLE} />
          {metrics.map((metric, index) => (
            <React.Fragment key={metric.key}>
              <Line dataKey={`${metric.key}_avg`} name={`${metric.label} average (${metric.unit})`} stroke={index === 0 ? 'var(--chart-3)' : 'var(--chart-5)'} yAxisId={metric.axis} connectNulls={false} />
              {!right && <Line dataKey={`${metric.key}_max`} name={`${metric.label} max (${metric.unit})`} stroke={index === 0 ? 'var(--chart-4)' : 'var(--chart-2)'} strokeDasharray="5 3" yAxisId={metric.axis} connectNulls={false} />}
            </React.Fragment>
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
};

const SourceCharts: React.FC<{ source: ReferenceSeries; dates: string[] }> = ({ source, dates }) => {
  const points = dates.map((date, index) => ({ date, day: source.days[index] ?? null }));
  if (points.every(point => point.day === null)) {
    return <p>{source.label} data covers {source.first_day} to {source.last_day}; none falls in these dates.</p>;
  }
  const indoor = source.kind === 'thermostat';
  const temperature: Metric = indoor ? 'indoor_temp_f' : 'temp_f';
  const humidity: Metric = indoor ? 'indoor_rh' : 'rh';
  return (
    <div>
      {source.kind !== 'air_monitor' && <RuntimePlot source={source} points={points} />}
      {source.kind === 'air_monitor' && AIR_METRICS.map(({ key, label, unit }) => (
        <StatsPlot key={key} title={`${source.label} — ${label}`} points={points} metrics={[{ key, label, unit, axis: 'left' }]} left={`${label} (${unit})`} />
      ))}
      <StatsPlot title={`${source.label} — Indoor temperature and humidity`} points={points} metrics={[
        { key: temperature, label: 'Temperature', unit: '°F', axis: 'left' },
        { key: humidity, label: 'Relative humidity', unit: '%', axis: 'right' },
      ]} left="Temperature (°F)" right="Relative humidity (%)" />
    </div>
  );
};

const ReferenceDataSection: React.FC<Props> = ({ deviceName, startDate, endDate, referenceError }) => {
  const [result, setResult] = useState<ReferenceResponse | null>(null);
  const [error, setError] = useState(referenceError ?? '');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (referenceError && attempt === 0) return;
    const controller = new AbortController();
    setResult(null);
    setError('');
    getReferenceData(deviceName, startDate, endDate, controller.signal)
      .then(data => setResult(data))
      .catch(err => { if (!controller.signal.aborted) setError(referenceErrorText(err)); });
    return () => controller.abort();
  }, [deviceName, startDate, endDate, referenceError, attempt]);

  if ((referenceError && attempt === 0) || error) {
    return <div role="alert" className="text-status-error-foreground">{(referenceError && attempt === 0) ? referenceError : error} <Button variant="link" title="Retry loading outside readings" onClick={() => setAttempt(value => value + 1)}>Retry</Button></div>;
  }
  if (!result) return <p role="status">Loading outside readings…</p>;
  return (
    <section aria-label="Reference data">
      <h3 className="text-lg font-semibold">Reference data</h3>
      <p className="text-sm text-muted-foreground">Outside readings uploaded for this Clip. Days are UTC days, the same days as the charts above.</p>
      {SOURCE_ORDER.flatMap(kind => result.sources.filter(source => source.kind === kind)).map(source => <SourceCharts key={source.kind} source={source} dates={result.dates} />)}
    </section>
  );
};

export default ReferenceDataSection;
